import { useEffect } from 'react';

const CANONICAL_BASE = 'https://datahireclub.com.br';

/**
 * Strips query string and hash fragment, normalizes trailing slash, and
 * returns an absolute canonical URL on the production domain. Canonicals must
 * always point to the clean URL — query params (utm_*, filtros, etc.) and
 * fragments are ignored to prevent duplicate-content signals.
 */
const stripQueryAndHash = (value: string) => value.split('?')[0].split('#')[0];

const normalizePath = (path: string) => {
  let p = path || '/';
  p = stripQueryAndHash(p);
  if (p.length > 1 && p.endsWith('/')) p = p.replace(/\/+$/, '');
  return p;
};

const buildCanonical = (override?: string) => {
  if (override) {
    if (override.startsWith('http')) {
      try {
        const url = new URL(override);
        return `${url.origin}${normalizePath(url.pathname)}`;
      } catch {
        return stripQueryAndHash(override);
      }
    }
    return `${CANONICAL_BASE}${normalizePath(override)}`;
  }
  if (typeof window === 'undefined') return CANONICAL_BASE;
  return `${CANONICAL_BASE}${normalizePath(window.location.pathname)}`;
};

/**
 * Lightweight hook that ensures a canonical link tag exists for the current
 * route without touching titles or descriptions. Use it on pages that already
 * manage their own <title>/<meta description> imperatively.
 */
export const useCanonicalTag = (canonical?: string) => {
  useEffect(() => {
    const href = buildCanonical(canonical);
    document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
    const link = document.createElement('link');
    link.rel = 'canonical';
    link.href = href;
    document.head.appendChild(link);

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.setAttribute('content', href);
  }, [canonical]);
};

/**
 * Marks a route as noindex,nofollow and removes any existing canonical tag.
 * Use on authenticated/admin routes that should never reach search results.
 */
export const useNoIndex = () => {
  useEffect(() => {
    document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow');
    return () => {
      const r = document.querySelector('meta[name="robots"]');
      if (r) r.remove();
    };
  }, []);
};
